import {
    ActivitySpinnerSize, ActivitySpinnerWidget,
    FilteringList,
    FilteringListProps,
    ListItem
  } from '@tableau/widgets';
  import React from 'react';
  import tabbable from 'tabbable';

import { Messages } from '~src/codegen/Localize';

import { SitePickerStyles } from '../../styles/SitePickerStyles';
import { LinkEnabledSitePickerListItem } from './LinkEnabledSitePicker';

export interface LinkEnabledSiteFilteringListProps extends FilteringListProps, Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
    readonly requestSitesAndTenantsInfo: () => void;
    readonly items: LinkEnabledSitePickerListItem[];
    readonly id: string;
}


export interface LinkEnabledSiteFilteringListState {
    readonly searchText: string;
    readonly filteredItems: ListItem[];
}

/**
 * Returns true if `item` should be included in the list of items matching `filterValue`.
 */
function caseInsensitiveValueMatch(item: LinkEnabledSitePickerListItem, filterValue: string): boolean {
  return item.displayName!.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0;
}

function shouldFilterOutTenantItem(item: LinkEnabledSitePickerListItem, filterValue: string, index: number, filteredItems: LinkEnabledSitePickerListItem[]): boolean {
    if (item.canAccess) {
    // filter out tenants that can access if no sites underneath and not matching filter value
        return (index < filteredItems.length - 1 && filteredItems[index + 1].separator && !caseInsensitiveValueMatch(item, filterValue)) ||
    (index === filteredItems.length-1 && !caseInsensitiveValueMatch(item, filterValue));
    }

    // filter out tenants that cant access if no sites underneath
    return ((index < filteredItems.length - 1 && filteredItems[index + 1].separator) || index === filteredItems.length-1);
}

// eslint-disable-next-line max-len
function sitePickerItemFilter(items: LinkEnabledSitePickerListItem[], filterValue: string): ListItem[] {
  return items.filter(item => // match keyword against site items
    item.group || item.separator || item.isTenant || caseInsensitiveValueMatch(item, filterValue)
   ).filter((item, index, filteredItems) => // filter out tenants that are not needed
    !item.isTenant || !shouldFilterOutTenantItem(item, filterValue, index, filteredItems)
  ).filter((item, index, filteredItems) => // filter out consecutive separators
    !(item.separator && index > 0 && filteredItems[index - 1].separator)
  ).filter((item, index, filteredItems) => // filter out list-initial and list-terminal separators
    !(item.separator && (index === 0 || index === filteredItems.length - 1))
  );
}

export class LinkEnabledSiteFilteringList extends React.Component<LinkEnabledSiteFilteringListProps, LinkEnabledSiteFilteringListState> {
    private containerElement: HTMLElement = null;

    public constructor(props: LinkEnabledSiteFilteringListProps) {
        super(props);
        this.state = {searchText: '', filteredItems: props.items};
    }

    public componentDidMount(): void {
      this.props.requestSitesAndTenantsInfo();
    }

    public componentDidUpdate(prevProps: Readonly<LinkEnabledSiteFilteringListProps>): void {
        if(prevProps.items !== this.props.items) {
            this.handleFilterChange(this.state.searchText);
        }
    }

    private handleFilterChange = (value: string): void => {
        const filteredItems = sitePickerItemFilter(this.props.items, value);
        if (filteredItems.length === 0) {
          filteredItems.push({ value: Messages.noMatches(), disabled: true });
        }
        this.setState({
          searchText: value,
          filteredItems,
        });
    };

    public render(): JSX.Element {
      const {requestSitesAndTenantsInfo,
        ...listProps } = this.props;

      if (this.props.items.length <= 0) {
        // show spinner until we have the items list
        return (
          <div className={SitePickerStyles.Spinner} >
            <ActivitySpinnerWidget
              spinnerSize={ActivitySpinnerSize.Small}
              testId="site-list-spinner" />
          </div>);
      }

      /* eslint-disable jsx-a11y/no-autofocus */
      /* eslint-disable jsx-a11y/no-static-element-interactions */
      return (
        <div
          className={SitePickerStyles.TenantEnabledPanel}
          data-tb-test-id="site-switcher-panel"
          ref={this.handleRef}
          onKeyDown={this.handleKeyDown}
          id={this.props.id}
        >
          <FilteringList {...listProps} items={this.state.filteredItems} autoFocus={true} style={SitePickerStyles.List}
            onFilterChange={this.handleFilterChange.bind(this)} searchText={this.state.searchText} />
        </div>
      );
    }

    private handleRef = (ref: HTMLElement | null): void => {
      this.containerElement = ref;
    };

    private handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
      if (e.key !== 'Tab') return;

      // Setting a focus trap to keep focus on the site picker flyout panel
      const tabbables = tabbable(this.containerElement);
      const first = tabbables.length > 0 ? tabbables[0] : null;
      const last = tabbables.length > 0 ? tabbables[tabbables.length - 1] : null;
      if (e.target === last && !e.shiftKey) {
        e.preventDefault();
        first.focus();
      } else if (e.target === first && e.shiftKey) {
        e.preventDefault();
        last.focus();
      }
    };
}
